/**
 * Normalizes a hostname from env or a request header into a bare, lowercase host
 * with no scheme, port, path, or surrounding whitespace.
 *
 * Why: Crane Manager hostname is consumed by four independent call sites (Better
 * Auth trusted origins, the cancel-order Slack link, the server host gate, and
 * the client host middleware). Each used to strip the `https://` prefix on its
 * own, or not at all, so a scheme-prefixed Bitwarden value could pass trusted
 * origins yet fail the host gate. One helper keeps them in agreement.
 */
export function normalizeHostname(value: string | undefined | null): string {
    return String(value || '')
        .trim()
        .replace(/^https?:\/\//i, '')
        .replace(/\/.*$/, '')
        .split(':')[0]!
        .trim()
        .toLowerCase()
}

/**
 * Designer-host Access model (Nick / TK-13512): Crane Manager runs on the same
 * hostname as the env’s designer app. Prefer an explicit
 * `NUXT_PUBLIC_CRANE_MANAGER_HOSTNAME` when Access destinations differ from
 * `designerBaseUrl` (e.g. prod `designer.app-crane.com` vs `app.crane.com`).
 */
export function resolveCraneManagerHostname(
    explicit: string | undefined | null,
    designerBaseUrl: string | undefined | null
): string {
    const fromExplicit = normalizeHostname(explicit)
    if (fromExplicit) {
        return fromExplicit
    }
    return normalizeHostname(designerBaseUrl)
}
